import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber, minsToHhMm, lastNDaysLabels } from '../SleepHub/sleepHubApi';
import { fetchBreathworkSessionsDashboard, secsToMmSs } from './breathworkHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  distributionBarOptions,
  donutOptions,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  CHART_LEGEND,
  paletteAt
} from '../../component/common/designSystem';
import {
  SESSION_TYPE_COLORS,
  SESSION_LENGTH_RAMP,
  CONSISTENCY_RAMP,
  STREAK_RAMP,
  BREATH_RAMP,
  TIME_OF_DAY_RAMP,
  experienceColor
} from './components/breathworkTheme';

/**
 * Breathwork → Sessions (Tracker) Dashboard.
 *
 * Reads as four bands, the same shape as the Sleep tracker:
 *  1. Headline stats — the practising population and its averages.
 *  2. Distributions — how sessions spread across length, time of day, streak,
 *     personal bests and how regularly people practise at all.
 *  3. Trends over the last 30 days — volume, technique split, minutes, weekday.
 *  4. Profiles — experience levels and what people came to breathwork for.
 *
 * Reused by the company panel via the `fetcher` prop (company-scoped data);
 * `populationLabel` names the scoped population (e.g. "employees").
 */
const SessionsDashboard = ({
  fetcher = fetchBreathworkSessionsDashboard,
  populationLabel = 'users'
}) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const sessionsPerDay = stats?.sessionsPerDay || [];
  const holdPerDay = stats?.holdPerDay || [];
  const exhalePerDay = stats?.exhalePerDay || [];
  const minutesPerDay = stats?.minutesPerDay || [];
  const weekday = stats?.weekdayPattern || [];
  const sessionLengths = stats?.sessionLengthDistribution || [];
  const timeOfDay = stats?.timeOfDayDistribution || [];
  const consistency = stats?.practiceConsistency || [];
  const streaks = stats?.streakDistribution || [];
  const holdBands = stats?.holdDistribution || [];
  const exhaleBands = stats?.exhaleDistribution || [];
  const experience = stats?.experienceDistribution || [];
  const goals = stats?.goalsDistribution || [];

  const trendLabels = lastNDaysLabels(sessionsPerDay.length);

  // Company view only: what share of the population has ever practised.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.usersActive / stats.totalUsers) * 100) : null;

  // People with a breathwork profile who have never completed a session — they
  // set up an intention and stopped, which is a nudge problem, not a usage one.
  const profiledNotPractising = Math.max(
    (stats?.profiledUsers || 0) - (stats?.usersActive || 0),
    0
  );

  /**
   * Daily volume, stacked by technique. Kept a pure bar chart rather than
   * overlaying the minutes line: ApexCharts stacks every series in a stacked
   * chart, line ones included, so the minutes get their own panel beside this.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'bar', stacked: true },
    colors: SESSION_TYPE_COLORS,
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    legend: CHART_LEGEND,
    grid: CHART_GRID,
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    // A stacked bar wants both techniques in one tooltip, but ApexCharts' bar
    // defaults turn `intersect` on and then refuse to pair it with `shared`,
    // so intersect has to be switched off explicitly here.
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val) => `${val.toLocaleString()} sessions` }
    }
  };

  /**
   * Weekday: volume as columns, average session length as a line on its own
   * axis — so a quiet Sunday with long sessions doesn't read as a bad day.
   */
  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.orange],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: weekday.map((day) => day.label) },
    yaxis: [
      { seriesName: 'Sessions', labels: { formatter: (val) => formatBigNumber(val) } },
      {
        seriesName: 'Avg length',
        opposite: true,
        labels: { formatter: (val) => secsToMmSs(val) }
      }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => `${val.toLocaleString()} sessions` },
        { formatter: (val) => secsToMmSs(val) }
      ]
    }
  };

  const timeOfDayOptions = distributionBarOptions({
    labels: timeOfDay.map((band) => band.label),
    colors: TIME_OF_DAY_RAMP,
    unit: 'sessions'
  });

  /**
   * Hold and exhale bests on one chart: they are the same measurement of the
   * same people, and side-by-side is the only way to see that a population is
   * strong at one and weak at the other.
   */
  const breathOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: SESSION_TYPE_COLORS,
    plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
    dataLabels: { enabled: false },
    legend: CHART_LEGEND,
    grid: CHART_GRID,
    xaxis: { categories: holdBands.map((band) => band.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} ${populationLabel}` } }
  };

  // Each experience bucket keeps its own colour wherever it appears.
  const experienceOptions = donutOptions({
    labels: experience.map((bucket) => bucket.label),
    colors: experience.map((bucket) => experienceColor(bucket.label)),
    unit: populationLabel
  });

  const goalsOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: goals.map((goal, index) => paletteAt(index)),
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true } },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: goals.map((goal) => goal.label) },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} ${populationLabel}` } }
  };

  const minutesOptions = {
    chart: { ...CHART_BASE, type: 'area' },
    colors: [CHART_COLORS.teal],
    stroke: { curve: 'smooth', width: 2 },
    fill: AREA_FILL,
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => minsToHhMm(val) } },
    tooltip: { y: { formatter: (val) => minsToHhMm(val) } }
  };

  const hasSessions = sessionsPerDay.some((count) => count > 0);

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Breathwork Sessions Dashboard | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Total Sessions"
          value={stats ? formatBigNumber(stats.totalSessions) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title={`${populationLabel === 'users' ? 'Users' : 'People'} Practising`}
          value={stats ? formatBigNumber(stats.usersActive) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Total Breathing Time"
          value={stats ? minsToHhMm(stats.totalSessionTimeMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Avg Session Length"
          value={stats ? secsToMmSs(stats.avgSessionSeconds) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Avg Current Streak"
          value={stats ? `${stats.avgStreak} days` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="Best Breath Hold"
          value={stats ? secsToMmSs(stats.bestHold) : '-'}
        />
      </div>

      {/* ---------- Distribution summary ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4 mt-6">
        <MiniDonutCard
          title="Session Length"
          subtitle="Seconds of breathing per session"
          data={sessionLengths}
          colors={SESSION_LENGTH_RAMP}
          unit="sessions"
        />
        <MiniDonutCard
          title="Time of Day"
          subtitle="When people practise"
          data={timeOfDay}
          colors={TIME_OF_DAY_RAMP}
          unit="sessions"
        />
        <MiniDonutCard
          title="Practice Consistency"
          subtitle="Sessions in the last 30 days"
          data={consistency}
          colors={CONSISTENCY_RAMP}
          unit={populationLabel}
        />
        <MiniDonutCard
          title="Current Streaks"
          subtitle="Consecutive days practising"
          data={streaks}
          colors={STREAK_RAMP}
          unit={populationLabel}
        />
        <MiniDonutCard
          title="Best Breath Hold"
          subtitle="Personal bests on record"
          data={holdBands}
          colors={BREATH_RAMP}
          unit={populationLabel}
        />
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel
          title="Sessions Per Day"
          subtitle="Breath Hold vs Breath Exhale — last 30 days"
        >
          {hasSessions ? (
            <Chart
              type="bar"
              height={340}
              width="100%"
              series={[
                { name: 'Breath Hold', data: holdPerDay },
                { name: 'Breath Exhale', data: exhalePerDay }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No session activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Breathing Time Per Day" subtitle="Minutes actually spent breathing">
          {hasSessions ? (
            <Chart
              type="area"
              height={340}
              width="100%"
              series={[{ name: 'Minutes practised', data: minutesPerDay }]}
              options={minutesOptions}
            />
          ) : (
            <PanelEmpty>No session activity yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Patterns ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {reachPercentage != null ? (
          <BigStatPanel
            title="Practice Reach"
            subtitle={`Share of ${populationLabel} who have practised`}
            value={`${reachPercentage}%`}
            caption={
              stats
                ? `${formatBigNumber(stats.usersActive)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
                : null
            }
          />
        ) : (
          <BigStatPanel
            title="Sessions That Stuck"
            subtitle={`Runs of ${stats?.meaningfulThresholdSeconds || 20}s or longer`}
            value={stats ? `${stats.meaningfulPercentage}%` : '-'}
            caption={
              stats
                ? `${formatBigNumber(stats.meaningfulSessions)} of ${formatBigNumber(
                  stats.totalSessions
                )} sessions`
                : null
            }
          />
        )}

        <Panel
          className="lg:col-span-2"
          title="Practice by Weekday"
          subtitle="Which days people breathe, and for how long"
        >
          {weekday.some((day) => day.sessions > 0) ? (
            <Chart
              type="line"
              height={300}
              width="100%"
              series={[
                { name: 'Sessions', type: 'column', data: weekday.map((day) => day.sessions) },
                {
                  name: 'Avg length',
                  type: 'line',
                  data: weekday.map((day) => day.avgSessionSeconds)
                }
              ]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No session activity yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Technique ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Panel title="When People Practise" subtitle="Sessions by time of day">
          {timeOfDay.some((band) => band.count > 0) ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Sessions', data: timeOfDay.map((band) => band.count) }]}
              options={timeOfDayOptions}
            />
          ) : (
            <PanelEmpty>No session activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Personal Bests" subtitle="Longest hold vs longest exhale">
          {holdBands.some((band) => band.count > 0) ||
          exhaleBands.some((band) => band.count > 0) ? (
              <Chart
                type="bar"
                height={300}
                width="100%"
                series={[
                  { name: 'Breath Hold', data: holdBands.map((band) => band.count) },
                  { name: 'Breath Exhale', data: exhaleBands.map((band) => band.count) }
                ]}
                options={breathOptions}
              />
            ) : (
              <PanelEmpty>No personal bests recorded yet.</PanelEmpty>
            )}
        </Panel>

        {/*
          Practice health, not headcount: the number that matters is how many
          people set up a breathwork profile and never went on to breathe.
        */}
        <Panel title="Practice Funnel" subtitle="From intention to a real practice">
          <ProgressList
            items={[
              { label: 'Practising', count: stats?.usersActive || 0 },
              { label: 'Set a profile, never practised', count: profiledNotPractising },
              {
                label: 'On a live streak',
                count: streaks
                  .filter((band) => band.label !== 'No streak')
                  .reduce((sum, band) => sum + band.count, 0)
              }
            ]}
            unit={populationLabel}
          />
        </Panel>
      </div>

      {/* ---------- Profiles ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Experience Levels" subtitle="From users' breathwork profiles">
          {experience.some((bucket) => bucket.count > 0) ? (
            <Chart
              type="donut"
              height={320}
              width="100%"
              series={experience.map((bucket) => bucket.count)}
              options={experienceOptions}
            />
          ) : (
            <PanelEmpty>No profile data yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Goals" subtitle="What people want from breathwork (multi-select)">
          {goals.some((goal) => goal.count > 0) ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[{ name: 'Selections', data: goals.map((goal) => goal.count) }]}
              options={goalsOptions}
            />
          ) : (
            <PanelEmpty>No profile data yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

SessionsDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default SessionsDashboard;
